import type {
  CaseReviewState,
  SourceDocumentRecord,
  StructuredDocumentRecord,
  SupportedDocumentType,
} from '@/features/case-workspace/types'

export const MVP_VISIBLE_DOCUMENT_TYPES: SupportedDocumentType[] = ['cbp_7501']

export function isMvpVisibleDocumentType(documentType: SupportedDocumentType) {
  return MVP_VISIBLE_DOCUMENT_TYPES.includes(documentType)
}

export function filterVisibleStructuredDocuments(records: StructuredDocumentRecord[]) {
  return records.filter((record) => isMvpVisibleDocumentType(record.documentType))
}

export function filterVisibleSourceDocuments(
  sourceDocuments: SourceDocumentRecord[],
  visibleStructuredDocuments: StructuredDocumentRecord[],
) {
  const visibleStructuredIds = new Set(visibleStructuredDocuments.map((record) => record.id))
  return sourceDocuments.filter((record) => {
    if (record.mirrorsDocumentType && isMvpVisibleDocumentType(record.mirrorsDocumentType)) return true
    if (record.linkedStructuredDocumentId && visibleStructuredIds.has(record.linkedStructuredDocumentId)) return true
    return false
  })
}

export function filterVisibleReviewState(
  state: CaseReviewState,
  visibleStructuredDocuments: StructuredDocumentRecord[],
) {
  const visibleDocumentIds = new Set(visibleStructuredDocuments.map((record) => record.id))
  const visibleFindings = state.findings.filter((finding) => {
    if (finding.documentType === 'case') return true
    if (finding.documentType && isMvpVisibleDocumentType(finding.documentType)) return true
    if (finding.documentId && visibleDocumentIds.has(finding.documentId)) return true
    return false
  })

  return {
    ...state,
    documentSummaries: state.documentSummaries.filter((summary) =>
      isMvpVisibleDocumentType(summary.documentType),
    ),
    missingRequirements: state.missingRequirements.filter((label) => label === 'CBP Form 7501'),
    incompleteDocuments: state.incompleteDocuments.filter((label) => label === 'CBP Form 7501'),
    readinessChecks: state.readinessChecks.filter((check) => check.label === 'CBP Form 7501'),
    findings: visibleFindings,
    reviewDecisions: state.reviewDecisions.filter((decision) =>
      visibleFindings.some((finding) => finding.id === decision.findingId),
    ),
    riskSummary: {
      total: visibleFindings.length,
      open: visibleFindings.filter((finding) => finding.status !== 'resolved').length,
      high: visibleFindings.filter(
        (finding) =>
          (finding.severity === 'high' || finding.severity === 'blocks_acceptance')
          && finding.status !== 'resolved',
      ).length,
    },
  } satisfies CaseReviewState
}
